import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  PieChart,
  RefreshCw,
  ChevronDown,
  ChevronRight,
  Globe,
  ArrowLeft,
  TrendingUp,
  TrendingDown,
} from 'lucide-react';

import { BACKEND_URL } from "../config/api";

const API = BACKEND_URL;

// Flag emoji mapping
const FLAG_EMOJIS = {
  AE: '🇦🇪', DE: '🇩🇪', GB: '🇬🇧', FR: '🇫🇷', EU: '🇪🇺', JP: '🇯🇵',
  KR: '🇰🇷', HK: '🇭🇰', CN: '🇨🇳', AU: '🇦🇺', SA: '🇸🇦', QA: '🇶🇦',
  TR: '🇹🇷', IL: '🇮🇱', ZA: '🇿🇦', US: '🇺🇸',
  AR: '🇦🇷', BR: '🇧🇷', CL: '🇨🇱', CO: '🇨🇴', PE: '🇵🇪', MX: '🇲🇽',
  IN: '🇮🇳', SG: '🇸🇬', TW: '🇹🇼', TH: '🇹🇭', MY: '🇲🇾', ID: '🇮🇩',
  PH: '🇵🇭', NZ: '🇳🇿', CA: '🇨🇦', RU: '🇷🇺', ES: '🇪🇸', IT: '🇮🇹',
  CH: '🇨🇭', NL: '🇳🇱', SE: '🇸🇪', PL: '🇵🇱', BE: '🇧🇪', AT: '🇦🇹',
  NO: '🇳🇴', DK: '🇩🇰', FI: '🇫🇮', PT: '🇵🇹', VN: '🇻🇳',
  GR: '🇬🇷', IE: '🇮🇪',
};

export default function ExchangeSectorPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const exchangeCode = searchParams.get('exchange') || 'DAX';
  
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedSectors, setExpandedSectors] = useState({});
  const [performanceTimeframe, setPerformanceTimeframe] = useState('day');
  const [exchangeList, setExchangeList] = useState([]);

  // Fetch exchange list
  useEffect(() => {
    const fetchExchangeList = async () => {
      try {
        const response = await axios.get(`${API}/api/market/exchange-list`);
        setExchangeList(response.data.exchanges || []);
      } catch (err) {
        console.error('Error fetching exchange list:', err);
      }
    };
    fetchExchangeList();
  }, []);

  // Fetch sector data
  const fetchSectorData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await axios.get(`${API}/api/market/exchange-sectors/${exchangeCode}`, {
        timeout: 30000
      });
      setData(response.data);
    } catch (err) {
      console.error('Error fetching exchange sectors:', err);
      setError(err.response?.data?.detail || 'Failed to load sector data');
    } finally {
      setLoading(false);
    }
  }, [exchangeCode]);

  useEffect(() => {
    fetchSectorData();
  }, [fetchSectorData]);

  // Toggle sector expansion
  const toggleSector = (sectorName) => {
    setExpandedSectors(prev => ({
      ...prev,
      [sectorName]: !prev[sectorName]
    }));
  };

  // Get performance value based on timeframe
  const getPerformanceValue = (sector) => {
    switch (performanceTimeframe) {
      case 'week': return sector.week_return;
      case 'month': return sector.month_return;
      case '3month': return sector.three_month_return;
      case '6month': return sector.six_month_return;
      case 'year': return sector.year_return;
      default: return sector.day_return;
    }
  };

  // Calculate sector rotation insights
  const getSectorRotationInsight = () => {
    if (!data?.sectors?.length) return null;
    
    // Filter out sectors with null/undefined day_return
    const validSectors = data.sectors.filter(s => s.day_return !== null && s.day_return !== undefined && !isNaN(s.day_return));
    
    if (validSectors.length === 0) return null;
    
    const topPerformers = validSectors
      .filter(s => s.day_return > 0)
      .sort((a, b) => b.day_return - a.day_return)
      .slice(0, 3);
    
    const bottomPerformers = validSectors
      .filter(s => s.day_return < 0)
      .sort((a, b) => a.day_return - b.day_return)
      .slice(0, 3);
    
    const avgReturn = validSectors.reduce((sum, s) => sum + s.day_return, 0) / validSectors.length;
    
    return {
      topPerformers,
      bottomPerformers,
      avgReturn,
      marketSentiment: avgReturn > 0.5 ? 'BULLISH' : avgReturn < -0.5 ? 'BEARISH' : 'NEUTRAL'
    };
  };

  const insight = getSectorRotationInsight();

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white">
      {/* Header */}
      <div className="bg-[#111] border-b border-gray-800 px-4 py-3">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate('/')}
              className="flex items-center gap-2 text-gray-400 hover:text-white transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
              <span className="text-sm">Back to Overview</span>
            </button>
            <div className="h-6 w-px bg-gray-700" />
            <div className="flex items-center gap-2">
              <Globe className="w-5 h-5 text-blue-400" />
              <span className="text-lg font-semibold">Exchange Sector Analysis</span>
            </div>
          </div>
          
          {/* Exchange Selector */}
          <div className="flex items-center gap-2">
            <label className="text-xs text-gray-400">Exchange:</label>
            <select
              value={exchangeCode}
              onChange={(e) => navigate(`/exchange-sectors?exchange=${e.target.value}`)}
              className="bg-[#1a1a2e] border border-gray-700 rounded-lg px-3 py-1.5 text-sm text-white focus:outline-none focus:border-blue-500"
              data-testid="exchange-selector"
            >
              {exchangeList.map(ex => (
                <option key={ex.code} value={ex.code}>
                  {FLAG_EMOJIS[ex.flag] || '🌍'} {ex.name} ({ex.code})
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-6">
        {/* Exchange Info Header */}
        {data && (
          <div className="bg-gradient-to-r from-[#1a1a2e] to-[#111] rounded-xl border border-gray-800 p-6 mb-6">
            <div className="flex items-start justify-between">
              <div>
                <div className="flex items-center gap-3 mb-2">
                  <span className="text-4xl">{FLAG_EMOJIS[data.flag] || '🌍'}</span>
                  <div>
                    <h1 className="text-2xl font-bold text-white">{data.exchange_name}</h1>
                    <p className="text-gray-400 text-sm">{data.country} • {data.currency}</p>
                  </div>
                </div>
                <p className="text-gray-500 text-sm mt-2">
                  Sector rotation analysis for identifying cross-market trends
                </p>
              </div>
              
              {/* Market Sentiment Badge */}
              {insight && (
                <div className={`px-4 py-2 rounded-lg border ${
                  insight.marketSentiment === 'BULLISH' 
                    ? 'bg-green-900/30 border-green-700 text-green-400' 
                    : insight.marketSentiment === 'BEARISH'
                    ? 'bg-red-900/30 border-red-700 text-red-400'
                    : 'bg-gray-800/50 border-gray-700 text-gray-300'
                }`}>
                  <div className="text-[10px] uppercase tracking-wider opacity-70">Market Sentiment</div>
                  <div className="flex items-center gap-2">
                    {insight.marketSentiment === 'BULLISH' ? (
                      <TrendingUp className="w-4 h-4" />
                    ) : insight.marketSentiment === 'BEARISH' ? (
                      <TrendingDown className="w-4 h-4" />
                    ) : null}
                    <span className="font-bold">{insight.marketSentiment}</span>
                    <span className="text-sm">({insight.avgReturn >= 0 ? '+' : ''}{insight.avgReturn.toFixed(2)}%)</span>
                  </div>
                </div>
              )}
            </div>

            {/* Sector Rotation Insight */}
            {insight && (
              <div className="grid grid-cols-2 gap-4 mt-6">
                {/* Top Performers */}
                <div className="bg-green-900/20 rounded-lg p-3 border border-green-800/50">
                  <div className="text-[10px] text-green-400 uppercase tracking-wider mb-2 flex items-center gap-1">
                    <TrendingUp className="w-3 h-3" />
                    Money Flowing Into (Sectors Leading)
                  </div>
                  <div className="space-y-1">
                    {insight.topPerformers.map((s, idx) => (
                      <div key={s.name} className="flex items-center justify-between text-sm">
                        <span className="text-gray-300">{idx + 1}. {s.name}</span>
                        <span className="text-green-400 font-medium">+{s.day_return.toFixed(2)}%</span>
                      </div>
                    ))}
                    {insight.topPerformers.length === 0 && (
                      <div className="text-gray-500 text-sm">No sectors gaining today</div>
                    )}
                  </div>
                </div>

                {/* Bottom Performers */}
                <div className="bg-red-900/20 rounded-lg p-3 border border-red-800/50">
                  <div className="text-[10px] text-red-400 uppercase tracking-wider mb-2 flex items-center gap-1">
                    <TrendingDown className="w-3 h-3" />
                    Money Flowing Out (Sectors Lagging)
                  </div>
                  <div className="space-y-1">
                    {insight.bottomPerformers.map((s, idx) => (
                      <div key={s.name} className="flex items-center justify-between text-sm">
                        <span className="text-gray-300">{idx + 1}. {s.name}</span>
                        <span className="text-red-400 font-medium">{s.day_return.toFixed(2)}%</span>
                      </div>
                    ))}
                    {insight.bottomPerformers.length === 0 && (
                      <div className="text-gray-500 text-sm">No sectors declining today</div>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Sector Performance Table */}
        <div className="bg-[#111] border border-gray-800 rounded-xl overflow-hidden">
          {/* Table Header */}
          <div className="bg-gradient-to-r from-[#1a1a2e] to-[#111] px-4 py-3 border-b border-gray-800 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <PieChart className="w-4 h-4 text-purple-500" />
              <span className="text-sm font-semibold text-white">Sector & Sub-Sector Performance</span>
              <span className="text-[10px] text-gray-500 ml-2">
                {data?.sector_count || 0} Sectors
              </span>
            </div>
            <div className="flex items-center gap-3">
              {/* Timeframe Toggle */}
              <div className="flex items-center bg-[#0d0d0d] rounded-lg p-0.5">
                {[
                  { key: 'day', label: '1D' },
                  { key: 'week', label: '1W' },
                  { key: 'month', label: '1M' },
                  { key: '3month', label: '3M' },
                  { key: '6month', label: '6M' },
                  { key: 'year', label: '1Y' }
                ].map(({ key, label }) => (
                  <button
                    key={key}
                    onClick={() => setPerformanceTimeframe(key)}
                    className={`px-2 py-1 text-[10px] font-medium rounded-md transition-all ${
                      performanceTimeframe === key
                        ? 'bg-purple-600 text-white'
                        : 'text-gray-400 hover:text-white'
                    }`}
                    data-testid={`timeframe-${key}`}
                  >
                    {label}
                  </button>
                ))}
              </div>
              {/* Refresh */}
              <button
                onClick={fetchSectorData}
                disabled={loading}
                className="text-gray-400 hover:text-white p-1 rounded hover:bg-gray-800"
                data-testid="refresh-sectors"
              >
                <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
              </button>
            </div>
          </div>

          {/* Content */}
          <div className="p-2">
            {loading ? (
              <div className="space-y-2" data-testid="sectors-skeleton">
                {Array.from({ length: 6 }).map((_, idx) => (
                  <div key={idx} className="flex items-center px-3 py-3 rounded-lg bg-gray-800/30 animate-pulse">
                    <div className="w-[30%] flex items-center gap-2">
                      <div className="w-4 h-4 bg-gray-700 rounded" />
                      <div className="h-4 w-32 bg-gray-700 rounded" />
                    </div>
                    <div className="w-[10%]">
                      <div className="h-4 w-10 bg-gray-700 rounded mx-auto" />
                    </div>
                    <div className="w-[10%]">
                      <div className="h-4 w-12 bg-gray-700 rounded ml-auto" />
                    </div>
                    <div className="w-[10%]">
                      <div className="h-4 w-12 bg-gray-700 rounded ml-auto" />
                    </div>
                    <div className="w-[10%]">
                      <div className="h-4 w-12 bg-gray-700 rounded ml-auto" />
                    </div>
                    <div className="w-[10%]">
                      <div className="h-4 w-12 bg-gray-700 rounded ml-auto" />
                    </div>
                    <div className="w-[10%]">
                      <div className="h-4 w-12 bg-gray-700 rounded ml-auto" />
                    </div>
                    <div className="w-[10%]">
                      <div className="h-4 w-12 bg-gray-700 rounded ml-auto" />
                    </div>
                  </div>
                ))}
              </div>
            ) : error ? (
              <div className="text-center py-12 text-red-400">
                <p>{error}</p>
                <button
                  onClick={fetchSectorData}
                  className="mt-4 px-4 py-2 bg-red-900/30 hover:bg-red-900/50 rounded-lg text-sm"
                >
                  Retry
                </button>
              </div>
            ) : data?.sectors?.length > 0 ? (
              <div className="space-y-1">
                {/* Table Header */}
                <div className="flex items-center px-3 py-2 text-[9px] text-gray-500 uppercase tracking-wider border-b border-gray-800">
                  <div className="w-[30%] min-w-[180px]">Sector / Sub-Sector</div>
                  <div className="w-[10%] text-center">ETF</div>
                  <div className="w-[10%] text-right">1D</div>
                  <div className="w-[10%] text-right">1W</div>
                  <div className="w-[10%] text-right">1M</div>
                  <div className="w-[10%] text-right">3M</div>
                  <div className="w-[10%] text-right">6M</div>
                  <div className="w-[10%] text-right">1Y</div>
                </div>

                {/* Sector Rows */}
                {data.sectors.map((sector) => {
                  // Safe number formatting helper
                  const formatReturn = (value) => {
                    if (value === null || value === undefined || isNaN(value)) return '-';
                    return `${value >= 0 ? '+' : ''}${value.toFixed(2)}%`;
                  };
                  const getReturnColor = (value) => {
                    if (value === null || value === undefined || isNaN(value)) return 'text-gray-500';
                    return value >= 0 ? 'text-green-400' : 'text-red-400';
                  };
                  
                  return (
                  <div key={sector.name} className="group">
                    {/* Main Sector Row */}
                    <div
                      className="flex items-center px-3 py-2.5 rounded-lg cursor-pointer transition-all hover:bg-gray-800/50"
                      style={{ 
                        borderLeft: `3px solid ${sector.color}`,
                        backgroundColor: expandedSectors[sector.name] ? 'rgba(31, 41, 55, 0.5)' : 'transparent'
                      }}
                      onClick={() => sector.subsectors?.length > 0 && toggleSector(sector.name)}
                      data-testid={`sector-row-${sector.name.replace(/\s+/g, '-').toLowerCase()}`}
                    >
                      <div className="w-[30%] min-w-[180px] flex items-center gap-2">
                        {sector.subsectors?.length > 0 && (
                          <button className="text-gray-400 hover:text-white">
                            {expandedSectors[sector.name] ? (
                              <ChevronDown className="w-4 h-4" />
                            ) : (
                              <ChevronRight className="w-4 h-4" />
                            )}
                          </button>
                        )}
                        <span className="text-xs font-medium text-white truncate">{sector.name}</span>
                        {sector.subsectors?.length > 0 && (
                          <span className="text-[9px] text-gray-500">
                            ({sector.subsectors.length})
                          </span>
                        )}
                      </div>
                      <div className="w-[10%] text-center">
                        {sector.etf && (
                          <a
                            href={`/stock?ticker=${sector.etf}`}
                            className="text-[11px] text-blue-400 hover:underline font-medium"
                            onClick={(e) => e.stopPropagation()}
                            data-testid={`etf-link-${sector.etf}`}
                          >
                            {sector.etf}
                          </a>
                        )}
                      </div>
                      <div className={`w-[10%] text-right text-xs font-medium ${getReturnColor(sector.day_return)}`}>
                        {formatReturn(sector.day_return)}
                      </div>
                      <div className={`w-[10%] text-right text-xs font-medium ${getReturnColor(sector.week_return)}`}>
                        {formatReturn(sector.week_return)}
                      </div>
                      <div className={`w-[10%] text-right text-xs font-medium ${getReturnColor(sector.month_return)}`}>
                        {formatReturn(sector.month_return)}
                      </div>
                      <div className={`w-[10%] text-right text-xs font-medium ${getReturnColor(sector.three_month_return)}`}>
                        {formatReturn(sector.three_month_return)}
                      </div>
                      <div className={`w-[10%] text-right text-xs font-medium ${getReturnColor(sector.six_month_return)}`}>
                        {formatReturn(sector.six_month_return)}
                      </div>
                      <div className={`w-[10%] text-right text-xs font-medium ${getReturnColor(sector.year_return)}`}>
                        {formatReturn(sector.year_return)}
                      </div>
                    </div>

                    {/* Expanded Subsectors */}
                    {expandedSectors[sector.name] && sector.subsectors?.length > 0 && (
                      <div className="ml-6 pl-3 border-l-2 border-gray-700 mb-2 mt-1">
                        {sector.subsectors.map((sub, idx) => {
                          // Safe formatting helpers
                          const formatSubReturn = (value) => {
                            if (value === null || value === undefined || isNaN(value)) return '-';
                            return `${value >= 0 ? '+' : ''}${value.toFixed(2)}%`;
                          };
                          const getSubReturnColor = (value) => {
                            if (value === null || value === undefined || isNaN(value)) return 'text-gray-500';
                            return value >= 0 ? 'text-green-400' : 'text-red-400';
                          };
                          
                          const subName = typeof sub === 'string' ? sub : sub?.name;
                          const subEtf = typeof sub === 'object' ? sub?.etf : sector.etf;
                          const subData = typeof sub === 'object' ? sub : null;
                          
                          return (
                            <div
                              key={subName || idx}
                              className="flex items-center px-3 py-2.5 rounded hover:bg-gray-800/30 transition-colors"
                              data-testid={`subsector-row-${subName?.replace(/\s+/g, '-').toLowerCase()}`}
                            >
                              <div className="w-[30%] min-w-[180px] flex items-center gap-2">
                                <PieChart className="w-3 h-3 text-gray-600" />
                                <span className="text-[11px] text-gray-300">{subName}</span>
                              </div>
                              <div className="w-[10%] text-center">
                                {subEtf && (
                                  <a
                                    href={`/stock?ticker=${subEtf}`}
                                    className="text-[11px] text-purple-400 hover:underline font-medium"
                                    data-testid={`subsector-etf-${subEtf}`}
                                  >
                                    {subEtf}
                                  </a>
                                )}
                              </div>
                              <div className={`w-[10%] text-right text-[11px] font-medium ${getSubReturnColor(subData?.day_return)}`}>
                                {formatSubReturn(subData?.day_return)}
                              </div>
                              <div className={`w-[10%] text-right text-[11px] font-medium ${getSubReturnColor(subData?.week_return)}`}>
                                {formatSubReturn(subData?.week_return)}
                              </div>
                              <div className={`w-[10%] text-right text-[11px] font-medium ${getSubReturnColor(subData?.month_return)}`}>
                                {formatSubReturn(subData?.month_return)}
                              </div>
                              <div className={`w-[10%] text-right text-[11px] font-medium ${getSubReturnColor(subData?.three_month_return)}`}>
                                {formatSubReturn(subData?.three_month_return)}
                              </div>
                              <div className={`w-[10%] text-right text-[11px] font-medium ${getSubReturnColor(subData?.six_month_return)}`}>
                                {formatSubReturn(subData?.six_month_return)}
                              </div>
                              <div className={`w-[10%] text-right text-[11px] font-medium ${getSubReturnColor(subData?.year_return)}`}>
                                {formatSubReturn(subData?.year_return)}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                  );
                })}
              </div>
            ) : (
              <div className="text-center py-12 text-gray-500">
                <Globe className="w-8 h-8 mx-auto mb-2 opacity-50" />
                <p>No sector data available for this exchange</p>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="px-4 py-2 bg-[#0a0a0a] border-t border-gray-800 text-[9px] text-gray-500 flex items-center justify-between">
            <span>
              Use sector rotation to identify global trends • Compare with US GICS sectors
            </span>
            <span>
              Updated: {data?.timestamp ? new Date(data.timestamp).toLocaleTimeString() : 'Loading...'}
            </span>
          </div>
        </div>

        {/* US Comparison Note */}
        <div className="mt-6 p-4 bg-blue-900/20 rounded-xl border border-blue-800/50">
          <div className="flex items-start gap-3">
            <div className="p-2 bg-blue-900/40 rounded-lg">
              <TrendingUp className="w-5 h-5 text-blue-400" />
            </div>
            <div>
              <h3 className="text-sm font-semibold text-blue-300 mb-1">Sector Rotation Analysis</h3>
              <p className="text-xs text-gray-400 leading-relaxed">
                Compare sector performance here with US GICS sectors on the Market Overview page. 
                When foreign markets show rotation into defensive sectors (Healthcare, Utilities) while US remains in growth sectors (Tech, Consumer Discretionary), 
                it may signal upcoming rotation in the US market. Look for leading indicators in Asian markets (Nikkei, KOSPI) 
                that often move 6-12 hours before US market open.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
